import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Home, Settings, Upload, X, Mail, Phone, Type, Palette, Image } from 'lucide-react';
import { CustomBreadcrumb, CustomInput, CustomButton } from '@/shared/components/atoms';
import { CustomCommonScreenHeader } from '@/shared/components/molecules';
import { settingsApi } from '../api/settingsApi';
import { SchoolSettingsUpdateRequest } from '../types';
import { useToastStore } from '@/shared/stores/toastStore';
import { useAuthStore } from '@/shared/stores/authStore';
import { getMediaUrl } from '@/shared/utils/media';

// Zod validation schema
const schoolSettingsSchema = z.object({
    name: z.string()
        .min(1, 'School name is required')
        .min(2, 'School name must be at least 2 characters'),
    tagline: z.string()
        .min(1, 'Tagline is required')
        .min(2, 'Tagline must be at least 2 characters')
        .max(200, 'Tagline must be under 200 characters'),
    support_email: z.string()
        .min(1, 'Support email is required')
        .email('Please enter a valid email address'),
    support_phone: z.string()
        .min(1, 'Support phone number is required')
        .regex(/^[+]?[\d\s\-().]{7,20}$/, 'Please enter a valid phone number'),
    primary_color: z.string()
        .min(1, 'Primary color is required')
        .regex(/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/, 'Please enter a valid hex color (e.g. #6366f1)'),
    accent_color: z.string()
        .min(1, 'Accent color is required')
        .regex(/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/, 'Please enter a valid hex color (e.g. #8b5cf6)'),
});

type SchoolSettingsFormData = z.infer<typeof schoolSettingsSchema>;

export const SchoolSettings = () => {
    const { user } = useAuthStore();
    const setTenant = useAuthStore((state) => state.setTenant);
    const { showToast } = useToastStore();
    const schoolSlug = user?.school?.slug || '';

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // File state (handled outside react-hook-form since they're File objects)
    const [logo, setLogo] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [banner, setBanner] = useState<File | null>(null);
    const [bannerPreview, setBannerPreview] = useState<string | null>(null);

    // Validation error state for logo/banner
    const [logoError, setLogoError] = useState<string | null>(null);
    const [bannerError, setBannerError] = useState<string | null>(null);

    const logoInputRef = useRef<HTMLInputElement>(null);
    const bannerInputRef = useRef<HTMLInputElement>(null);

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
        reset,
    } = useForm<SchoolSettingsFormData>({
        resolver: zodResolver(schoolSettingsSchema),
        defaultValues: {
            name: '',
            tagline: '',
            support_email: '',
            support_phone: '',
            primary_color: '#6366f1',
            accent_color: '#8b5cf6',
        }
    });

    const primaryColor = watch('primary_color');
    const accentColor = watch('accent_color');
    const tagline = watch('tagline') || '';

    useEffect(() => {
        if (schoolSlug) {
            fetchSettings();
        }
    }, [schoolSlug]);

    const fetchSettings = async () => {
        setIsLoading(true);
        try {
            const response = await settingsApi.getSettings(schoolSlug);
            if (response.success && response.data) {
                const data = response.data;
                reset({
                    name: data.name || '',
                    tagline: data.tagline || '',
                    support_email: data.support_email || '',
                    support_phone: data.support_phone || '',
                    primary_color: data.primary_color || '#6366f1',
                    accent_color: data.accent_color || '#8b5cf6',
                });
                if (data.logo_url) setLogoPreview(getMediaUrl(data.logo_url) || null);
                if (data.banner_url) setBannerPreview(getMediaUrl(data.banner_url) || null);
                setLogoError(null);
                setBannerError(null);
            }
        } catch (error) {
            showToast('Failed to load settings', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                showToast('Logo file must be under 2MB', 'error');
                setLogoError('Logo file must be under 2MB');
                return;
            }
            setLogo(file);
            setLogoError(null);
            const reader = new FileReader();
            reader.onloadend = () => setLogoPreview(reader.result as string);
            reader.readAsDataURL(file);
        }
    };

    const handleBannerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                showToast('Banner file must be under 5MB', 'error');
                setBannerError('Banner file must be under 5MB');
                return;
            }
            setBanner(file);
            setBannerError(null);
            const reader = new FileReader();
            reader.onloadend = () => setBannerPreview(reader.result as string);
            reader.readAsDataURL(file);
        }
    };

    const removeLogo = () => {
        setLogo(null);
        setLogoPreview(null);
        setLogoError('School logo is required');
        if (logoInputRef.current) logoInputRef.current.value = '';
    };

    const removeBanner = () => {
        setBanner(null);
        setBannerPreview(null);
        setBannerError('School banner is required');
        if (bannerInputRef.current) bannerInputRef.current.value = '';
    };

    const onSubmit = async (data: SchoolSettingsFormData) => {
        if (!schoolSlug) {
            showToast('School context not found', 'error');
            return;
        }

        let hasError = false;
        if (!logoPreview) {
            setLogoError('School logo is required');
            hasError = true;
        }
        if (!bannerPreview) {
            setBannerError('School banner is required');
            hasError = true;
        }

        if (hasError) {
            showToast('Please fix the validation errors before saving', 'error');
            return;
        }

        setIsSaving(true);
        try {
            const payload: SchoolSettingsUpdateRequest = {
                name: data.name.trim(),
                primary_color: data.primary_color,
                accent_color: data.accent_color,
                tagline: (data.tagline || '').trim(),
                support_email: (data.support_email || '').trim(),
                support_phone: (data.support_phone || '').trim(),
            };

            if (logo) payload.logo = logo;
            if (banner) payload.banner = banner;

            const response = await settingsApi.updateSettings(schoolSlug, payload);
            if (response.success) {
                showToast('Settings updated successfully!', 'success');

                // Update the tenant store so changes reflect across the app immediately
                setTenant({
                    name: data.name.trim(),
                    slug: schoolSlug,
                    primary_color: data.primary_color,
                    accent_color: data.accent_color,
                    tagline: (data.tagline || '').trim(),
                    support_email: (data.support_email || '').trim(),
                    support_phone: (data.support_phone || '').trim(),
                    logo_url: response.data?.logo_url || logoPreview || undefined,
                    banner_url: response.data?.banner_url || bannerPreview || undefined,
                });

                // If the API returned updated data, sync previews
                if (response.data) {
                    if (response.data.logo_url) setLogoPreview(getMediaUrl(response.data.logo_url) || null);
                    if (response.data.banner_url) setBannerPreview(getMediaUrl(response.data.banner_url) || null);
                }

                // Clear file selections after successful upload
                setLogo(null);
                setBanner(null);
                if (logoInputRef.current) logoInputRef.current.value = '';
                if (bannerInputRef.current) bannerInputRef.current.value = '';
            } else {
                showToast(response.error || 'Failed to update settings', 'error');
            }
        } catch (error) {
            showToast('An unexpected error occurred', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div style={{ padding: '1rem 1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.9375rem' }}>Loading settings...</div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ padding: '1rem 1.5rem' }}>
            <CustomBreadcrumb
                items={[
                    { label: 'Home', path: '/', icon: <Home size={14} /> },
                    { label: 'School Settings', icon: <Settings size={14} /> }
                ]}
            />

            <CustomCommonScreenHeader
                title="School Settings"
                subtitle="Configure your school's branding, contact information, and appearance."
                icon={<Settings size={24} />}
            />

            <div className="school-settings-card">
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="school-settings-grid">

                        {/* School Name */}
                        <div style={{ gridColumn: '1 / -1' }}>
                            <CustomInput
                                label="School Name"
                                placeholder="Enter school name"
                                leftIcon={<Type size={18} />}
                                error={errors.name?.message}
                                required
                                {...register('name')}
                            />
                        </div>

                        {/* Tagline */}
                        <div style={{ gridColumn: '1 / -1' }}>
                            <CustomInput
                                label="Tagline"
                                placeholder="Enter school tagline"
                                leftIcon={<Type size={18} />}
                                error={errors.tagline?.message}
                                helperText={`${tagline.length}/200 characters`}
                                required
                                {...register('tagline')}
                            />
                        </div>

                        {/* Support Email */}
                        <div style={{ gridColumn: '1 / -1' }}>
                            <CustomInput
                                label="Support Email"
                                type="email"
                                placeholder="support@school.com"
                                leftIcon={<Mail size={18} />}
                                error={errors.support_email?.message}
                                required
                                {...register('support_email')}
                            />
                        </div>

                        {/* Support Phone */}
                        <div style={{ gridColumn: '1 / -1' }}>
                            <CustomInput
                                label="Support Phone"
                                placeholder="+1 234 567 890"
                                leftIcon={<Phone size={18} />}
                                error={errors.support_phone?.message}
                                required
                                {...register('support_phone')}
                            />
                        </div>

                        {/* Colors Section */}
                        <div style={{ gridColumn: '1 / -1', marginTop: '0.5rem' }}>
                            <label style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '1rem', display: 'block' }}>
                                <Palette size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                                Brand Colors
                            </label>
                            <div className="school-settings-colors-grid">
                                <div>
                                    <label style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.5rem', display: 'block' }}>
                                        Primary Color <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
                                        <input
                                            type="color"
                                            value={primaryColor || '#6366f1'}
                                            onChange={(e) => setValue('primary_color', e.target.value, { shouldValidate: true })}
                                            style={{
                                                width: '44px',
                                                height: '44px',
                                                minWidth: '44px',
                                                minHeight: '44px',
                                                border: '2px solid var(--border-color)',
                                                borderRadius: '10px',
                                                cursor: 'pointer',
                                                padding: '2px',
                                                background: 'var(--card-bg)',
                                                flexShrink: 0
                                            }}
                                        />
                                        <input
                                            type="text"
                                            placeholder="#6366f1"
                                            {...register('primary_color')}
                                            style={{
                                                flex: 1,
                                                minWidth: 0,
                                                padding: '0.625rem 0.875rem',
                                                border: `1px solid ${errors.primary_color ? 'var(--status-error, #ef4444)' : 'var(--border-color)'}`,
                                                borderRadius: '8px',
                                                fontSize: '0.875rem',
                                                background: 'var(--input-bg, var(--card-bg))',
                                                color: 'var(--text-primary)',
                                                fontFamily: 'monospace'
                                            }}
                                        />
                                    </div>
                                    {errors.primary_color && (
                                        <p style={{ color: 'var(--status-error, #ef4444)', fontSize: '0.75rem', marginTop: '0.35rem' }}>{errors.primary_color.message}</p>
                                    )}
                                </div>
                                <div>
                                    <label style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.5rem', display: 'block' }}>
                                        Accent Color <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
                                        <input
                                            type="color"
                                            value={accentColor || '#8b5cf6'}
                                            onChange={(e) => setValue('accent_color', e.target.value, { shouldValidate: true })}
                                            style={{
                                                width: '44px',
                                                height: '44px',
                                                minWidth: '44px',
                                                minHeight: '44px',
                                                border: '2px solid var(--border-color)',
                                                borderRadius: '10px',
                                                cursor: 'pointer',
                                                padding: '2px',
                                                background: 'var(--card-bg)',
                                                flexShrink: 0
                                            }}
                                        />
                                        <input
                                            type="text"
                                            placeholder="#8b5cf6"
                                            {...register('accent_color')}
                                            style={{
                                                flex: 1,
                                                minWidth: 0,
                                                padding: '0.625rem 0.875rem',
                                                border: `1px solid ${errors.accent_color ? 'var(--status-error, #ef4444)' : 'var(--border-color)'}`,
                                                borderRadius: '8px',
                                                fontSize: '0.875rem',
                                                background: 'var(--input-bg, var(--card-bg))',
                                                color: 'var(--text-primary)',
                                                fontFamily: 'monospace'
                                            }}
                                        />
                                    </div>
                                    {errors.accent_color && (
                                        <p style={{ color: 'var(--status-error, #ef4444)', fontSize: '0.75rem', marginTop: '0.35rem' }}>{errors.accent_color.message}</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Logo Upload */}
                        <div style={{ gridColumn: '1 / -1', marginTop: '0.5rem' }}>
                            <label style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block' }}>
                                School Logo <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                {logoPreview ? (
                                    <div style={{ position: 'relative', width: '80px', height: '80px', borderRadius: '12px', overflow: 'hidden', border: '2px solid var(--border-color)' }}>
                                        <img src={logoPreview} alt="Logo Preview" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '4px' }} />
                                        <button
                                            type="button"
                                            onClick={removeLogo}
                                            style={{
                                                position: 'absolute', top: '4px', right: '4px', background: 'rgba(0,0,0,0.5)', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'
                                            }}
                                        >
                                            <X size={12} />
                                        </button>
                                    </div>
                                ) : (
                                    <div
                                        style={{ width: '80px', height: '80px', borderRadius: '12px', background: 'var(--bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px dashed var(--border-color)', cursor: 'pointer', color: 'var(--text-muted)' }}
                                        onClick={() => logoInputRef.current?.click()}
                                    >
                                        <Image size={24} />
                                    </div>
                                )}
                                <div>
                                    <CustomButton type="button" variant="outline" size="small" onClick={() => logoInputRef.current?.click()}>
                                        Upload Logo
                                    </CustomButton>
                                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>PNG, JPG, SVG — max 2MB</p>
                                </div>
                                <input
                                    type="file"
                                    ref={logoInputRef}
                                    onChange={handleLogoChange}
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                />
                            </div>
                            {logoError && (
                                <p style={{ color: 'var(--status-error, #ef4444)', fontSize: '0.75rem', marginTop: '0.5rem' }}>{logoError}</p>
                            )}
                        </div>

                        {/* Banner Upload */}
                        <div style={{ gridColumn: '1 / -1', marginTop: '0.5rem' }}>
                            <label style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block' }}>
                                School Banner <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                {bannerPreview ? (
                                    <div style={{ position: 'relative', width: '100%', height: '140px', borderRadius: '12px', overflow: 'hidden', border: '2px solid var(--border-color)' }}>
                                        <img src={bannerPreview} alt="Banner Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        <button
                                            type="button"
                                            onClick={removeBanner}
                                            style={{
                                                position: 'absolute', top: '8px', right: '8px', background: 'rgba(0,0,0,0.5)', color: 'white', border: 'none', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'
                                            }}
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                ) : (
                                    <div
                                        style={{ width: '100%', height: '140px', borderRadius: '12px', background: 'var(--bg-secondary)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '2px dashed var(--border-color)', cursor: 'pointer', color: 'var(--text-muted)', gap: '0.5rem' }}
                                        onClick={() => bannerInputRef.current?.click()}
                                    >
                                        <Upload size={28} />
                                        <span style={{ fontSize: '0.8125rem' }}>Click to upload banner</span>
                                    </div>
                                )}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                                    <CustomButton type="button" variant="outline" size="small" onClick={() => bannerInputRef.current?.click()}>
                                        Upload Banner
                                    </CustomButton>
                                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Recommended: 1200×300px, PNG or JPG — max 5MB</p>
                                </div>
                                <input
                                    type="file"
                                    ref={bannerInputRef}
                                    onChange={handleBannerChange}
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                />
                            </div>
                            {bannerError && (
                                <p style={{ color: 'var(--status-error, #ef4444)', fontSize: '0.75rem', marginTop: '0.5rem' }}>{bannerError}</p>
                            )}
                        </div>

                        {/* Submit */}
                        <div style={{
                            gridColumn: '1 / -1',
                            marginTop: '2rem',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '1rem',
                            borderTop: '1px solid var(--border-color)',
                            paddingTop: '2rem'
                        }}>
                            <CustomButton
                                type="submit"
                                variant="filled"
                                color="primary"
                                loading={isSaving}
                            >
                                Save Settings
                            </CustomButton>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};
